import Alpine from '@alpinejs/csp';
import AsyncAlpine from 'async-alpine';
import collapse from '@alpinejs/collapse';
import intersect from '@alpinejs/intersect';
import focus from '@alpinejs/focus';
import { createRouter, PAGE_VIEW_EVENT } from './router/swup.js';
import { initScrollReveals } from './transitions/scroll-reveal.js';
import { initHorizontalScrollers } from './transitions/horizontal-scroller.js';
import { initColourReveal } from './components/colour-reveal.js';
import { initEnquiryContext } from './components/enquiry-context.js';
import { initNavActiveSync } from './components/nav-active-sync.js';
import { initPageviewTracking } from './analytics/virtual-pageview.js';
import { initEventTracking } from './analytics/events.js';

// Level 2 component imports (eagerly loaded, registered before Alpine.start())
import accordion from './components/accordion.js';
import dropdown from './components/dropdown.js';
import tabs from './components/tabs.js';
import toggle from './components/toggle.js';
import modal from './components/modal.js';
import mobileNav from './components/mobile-nav.js';
import siteMenu from './components/site-menu.js';
import announcementBar from './components/announcement-bar.js';
import themeToggle from './components/theme-toggle.js';
import toastContainer from './components/toast-container.js';
import video from './components/video.js';
import carousel from './components/carousel.js';
import share from './components/share.js';
import loadMore from './components/load-more.js';
// Library tier (inert): unwired in kit chrome, CSS only in docs.css. Registered so
// its /_docs demo route works with the real bundle; opt in per project. See anchor-nav.js.
import anchorNav from './components/anchor-nav.js';
import headroomHeader from './components/headroom-header.js';

/*
 * PSD-Stack — App Bootstrap
 *
 * Initialisation order matters:
 * 1. Alpine plugins (must register before Alpine.start())
 * 2. Alpine stores (must register before Alpine.start())
 * 3. Alpine.data() component registrations
 * 4. Async Alpine (must init before Alpine.start())
 * 5. Alpine.start()
 * 6. Swup router (after DOM is ready)
 * 7. Analytics (pageview tracking + structural events + deferred GTM)
 * 8. Deferred: GSAP (requestIdleCallback)
 * 9. Deferred: Three.js (desktop only, requestIdleCallback)
 */

// 1. Register Alpine plugins
Alpine.plugin(collapse);
Alpine.plugin(intersect);
Alpine.plugin(focus);

// 2. Register Alpine stores (persist across Swup page transitions)
// `menuOpen` drives GNG's header MENU disclosure; `mobileOpen` is the kit's
// drawer, still registered and no longer rendered here. Both live in ONE store
// because router/swup.js closes them on `visit:start` from a single lookup.
Alpine.store('nav', { mobileOpen: false, menuOpen: false });

Alpine.store('toasts', {
    items: [],
    counter: 0,

    add(message, type = 'info', duration = 5000) {
        const id = ++this.counter;
        this.items.push({ id, message, type });

        if (duration > 0) {
            setTimeout(() => this.remove(id), duration);
        }
    },

    remove(id) {
        this.items = this.items.filter((t) => t.id !== id);
    },

    success(message) {
        this.add(message, 'success');
    },

    error(message) {
        this.add(message, 'error', 0);
    },

    warning(message) {
        this.add(message, 'warning');
    },
});

// 3. Register Level 2 component definitions
Alpine.data('accordion', accordion);
Alpine.data('dropdown', dropdown);
Alpine.data('tabs', tabs);
Alpine.data('toggle', toggle);
Alpine.data('modal', modal);
Alpine.data('mobileNav', mobileNav);
Alpine.data('siteMenu', siteMenu);
Alpine.data('announcementBar', announcementBar);
Alpine.data('themeToggle', themeToggle);
Alpine.data('toastContainer', toastContainer);
Alpine.data('video', video);
Alpine.data('carousel', carousel);
Alpine.data('share', share);
Alpine.data('loadMore', loadMore);
Alpine.data('anchorNav', anchorNav); // library tier — see import note above
Alpine.data('headroomHeader', headroomHeader); // library tier — see import note above

// 4. Initialise Async Alpine (lazy component loading)
Alpine.plugin(AsyncAlpine);
Alpine.asyncData('gallery', () => import('./components/gallery.js'));
Alpine.asyncData('form', () => import('./components/form.js'));
// The offices map: the heaviest dependency on the site, so it is not merely
// async but `x-load="visible"` in the markup — the Google SDK is not requested
// until the offices section is scrolled into view. See the module header.
Alpine.asyncData('officeMap', () => import('./components/office-map.js'));

// 5. Start Alpine
Alpine.start();

// Make Alpine available globally for debugging
window.Alpine = Alpine;

// 6. Initialise Swup router
const swup = createRouter();

// Re-init Alpine on Swup page navigation
swup.hooks.before('content:replace', () => {
    Alpine.destroyTree(document.querySelector('#swup'));
});
swup.hooks.on('content:replace', () => {
    Alpine.initTree(document.querySelector('#swup'));
});

// 7. Analytics
initPageviewTracking(swup);
initEventTracking(swup);

// Colour-reveal image placeholders. GSAP-free (plain IntersectionObserver + CSS
// opacity), so it runs on first paint without waiting for the deferred GSAP
// bundle, and re-runs on every psd:page-view to enhance Swup-swapped content.
initColourReveal();
window.addEventListener(PAGE_VIEW_EVENT, initColourReveal);

// Enquiry-context hydration. Server-first "enquiry store": copies the per-page
// #enquiry-context values (plus once-captured UTM params) into hidden inputs of
// every POST form on load and on every psd:page-view — covering both forms inside
// #swup and a persistent global modal form outside it. Plain module, not an Alpine
// directive (CSP-safe). See components/enquiry-context.js.
initEnquiryContext();
window.addEventListener(PAGE_VIEW_EVENT, initEnquiryContext);

// Nav active-link sync. The header/nav renders outside #swup, so its
// server-rendered aria-current + active styling freeze after the first soft
// navigation; this re-derives the active link from the URL on every
// psd:page-view. Self-registers the listener. See components/nav-active-sync.js.
initNavActiveSync();

// Defer GTM to idle — Tier 4 asset
if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
        import('./analytics/gtm.js').then(({ loadGTM }) => {
            loadGTM(import.meta.env.VITE_GTM_ID);
        });
    });
} else {
    setTimeout(() => {
        import('./analytics/gtm.js').then(({ loadGTM }) => {
            loadGTM(import.meta.env.VITE_GTM_ID);
        });
    }, 2000);
}

// ScrollTrigger lifecycle — kill instances before content swap, refresh after
swup.hooks.on('content:replace', () => {
    if (window.ScrollTrigger) {
        window.ScrollTrigger.getAll().forEach((st) => st.kill());
    }
});

swup.hooks.on('page:view', () => {
    if (window.ScrollTrigger) {
        window.ScrollTrigger.refresh();
    }
    initScrollReveals();
    initHorizontalScrollers();
});

// 8. Deferred: GSAP + ScrollTrigger + Flip (loaded on the first idle moment)
//
// TRIED AND REVERTED, 28 Aug 2026: loading these on the first interaction
// (`pointerdown` / `scroll` / a timeout) instead of on idle. It looked like the
// obvious answer to Lighthouse's `unused-javascript`, and it was not.
//
// It bought nothing measurable — Lighthouse SCROLLS the page during its own
// audit, so the listener fired and GSAP was fetched inside the load window
// regardless — and it cost something real: `pointerdown` fires about a tenth of
// a second before the visit it precedes, which is not enough to fetch 135 KB on
// a slow connection, so the FIRST navigation would have been unanimated. Idle
// loading has it ready long before.
//
// The 208 KB the audit was actually complaining about was Three.js, and section
// 9 below is where that was fixed.
function loadGsap() {
    Promise.all([
        import('gsap'),
        import('gsap/ScrollTrigger'),
        import('gsap/Flip'),
    ]).then(([{ gsap }, { ScrollTrigger }, { Flip }]) => {
        gsap.registerPlugin(ScrollTrigger, Flip);
        window.gsap = gsap;
        window.ScrollTrigger = ScrollTrigger;
        window.Flip = Flip;
        initScrollReveals();
        // The pinned horizontal run behind the timeline layout. Lives here rather
        // than at module top level because it needs `window.gsap` and
        // `window.ScrollTrigger`, which do not exist until this promise resolves.
        initHorizontalScrollers();
    });
}

if ('requestIdleCallback' in window) {
    requestIdleCallback(loadGsap);
} else {
    setTimeout(loadGsap, 1000);
}

// 9. Deferred: Shader system — capability-gated AND target-gated.
//
// THE TARGET GATE IS NEW, and the capability gate was doing half a job without
// it. Every effect module imports Three.js, so importing one to discover that
// its <canvas> is not on the page costs 126 KB gzipped for nothing. A page with
// no shader canvas paid it in full on a machine whose GPU happened to pass the
// probe — which is most of them. Lighthouse found it: 208 KB of unused
// JavaScript on the home page, all of it Three.
//
// Checked BEFORE the import rather than inside `manager.register`, because by
// then the module is already downloaded and parsed. The probe stays first: it is
// two orders of magnitude cheaper than either.
const SHADER_TARGETS = ['#hero-shader', '#shader-canvas'];

if ('requestIdleCallback' in window && SHADER_TARGETS.some((t) => document.querySelector(t))) {
    requestIdleCallback(async () => {
        try {
            const { runProbe } = await import('./shaders/probe.js');
            const probeResult = await runProbe();

            if (!probeResult.capable) return;

            const { createRenderManager } = await import('./shaders/manager.js');
            const manager = createRenderManager(probeResult);

            // Init bridges (each guards against missing dependencies)
            const { initSwupBridge } = await import('./shaders/bridges/swup-bridge.js');
            initSwupBridge(manager, swup);

            const { initGsapBridge } = await import('./shaders/bridges/gsap-bridge.js');
            initGsapBridge(manager);

            // Register effects — each targets a <canvas> element in the DOM, and
            // each is imported ONLY when that element is there to render into.
            if (document.querySelector('#hero-shader')) {
                const { NoiseBgEffect } = await import('./shaders/effects/noise-bg.js');
                manager.register(new NoiseBgEffect(), { target: '#hero-shader' });
            }

            if (document.querySelector('#shader-canvas')) {
                const { DisplacementTransitionEffect } = await import('./shaders/effects/displacement-transition.js');
                manager.register(new DisplacementTransitionEffect(), { target: '#shader-canvas' });
            }

            // Expose for dev tools
            if (import.meta.env.DEV) {
                window.__shaderManager = manager;
            }
        } catch (err) {
            if (import.meta.env.DEV) {
                console.warn('[Shaders] Initialisation failed:', err);
            }
        }
    });
}
